import React from 'react';
import Avatar from '@mui/material/Avatar';

import { AvatarByName, avatarInitial, resolveEventPlayer } from './avatar';
import { CardStrip, HoleCards } from './Cards';
import { formatEventTime, formatPot } from './format';
import { BettingRound, groupByBettingRound, isStreetEvent } from './playByPlayRounds';
import { CardRef, GameType, HandHistoryEvent } from './types';
import { RunResult } from './winSummary';

import styles from './HandHistory.module.scss';

// Same fallback as Game Chat's message avatars: the picture when there is one,
// the player's initial otherwise (MUI swaps to the children on a load error).
const rowAvatarSx = {
  width: 22,
  height: 22,
  fontSize: '0.65rem',
  fontWeight: 700,
  bgcolor: 'rgba(255, 255, 255, 0.1)',
  color: 'rgba(255, 255, 255, 0.9)'
};

const RowLead = ({
  event,
  avatarByName
}: {
  event: HandHistoryEvent;
  avatarByName?: AvatarByName;
}) => {
  const player = resolveEventPlayer(event, avatarByName);
  return player ? (
    <span className={styles.eventAvatar}>
      <Avatar sx={rowAvatarSx} src={player.src} alt=''>
        {avatarInitial(player.name)}
      </Avatar>
    </span>
  ) : (
    <span className={styles.eventDot} data-type={event.type} />
  );
};

const RowTrail = ({ event, gameType }: { event: HandHistoryEvent; gameType: GameType }) =>
  typeof event.totalPot === 'number' ? (
    <span className={styles.playByPlayPot}>{formatPot(event.totalPot, gameType)}</span>
  ) : (
    <span className={styles.playByPlayType}>{event.type}</span>
  );

/**
 * The play-by-play, oldest first, one container per betting round.
 *
 * Cory asked for the hand to read top-down and Jason agreed on 28 Sep; the
 * demo room wanted the streets told apart (Victor: "same indentation, the same
 * level, same coloring"; Ryan: "if that section by itself was a different
 * color, it'd be a lot easier to read"). So each round is a box with a header
 * — the street's name and the board as dealt so far, the hole cards on the
 * pre-flop round when the viewer's are known — and the actions inside it sit
 * on divider lines instead of each being its own bubble. The "Dealing …" text
 * stays for screen readers.
 */
export const PlayByPlayList = ({
  events,
  gameType,
  avatarByName,
  showTableCards = false,
  holeCards,
  runResults,
  runBoards
}: {
  events: HandHistoryEvent[];
  gameType: GameType;
  /** Display name → avatar; a player's action row shows theirs, like the production Table History. */
  avatarByName?: AvatarByName;
  /** Opt-in for V1/proposal samples: the older flat list with cards under any row that carries them. */
  showTableCards?: boolean;
  /** The viewer's hole cards, shown on the pre-flop round's header. */
  holeCards?: CardRef[];
  /**
   * Each paid run's result, by run number: what the viewer won on it ("You
   * won: Main, Side"), else who did ("Aaric_G won Main, Side"), and what that
   * run paid. A run with no result yet has no result line.
   */
  runResults?: Record<number, RunResult>;
  /** Every board of a live run-out, so the runs are laid out before any has paid. */
  runBoards?: CardRef[][];
}) => {
  if (showTableCards) {
    return <FlatPlayByPlayList events={events} gameType={gameType} avatarByName={avatarByName} />;
  }

  const rounds = groupByBettingRound(events, holeCards, { runBoards });
  // One key per round that does not change as the round fills in. A run is
  // keyed by its number: on a live run-out every run not yet logged is laid
  // out from the felt's boards with no street row, and keying those by
  // `street.id ?? key` gave them all the key "run". React cannot tell duplicate
  // keys apart, so when one of them gained its "Run n" row it kept the old
  // section as well — "Run 2" twice, the stale copy without time, amount or
  // payouts (screenshot #17).
  const roundKey = (round: BettingRound) =>
    round.run !== undefined ? `run-${round.run}` : round.street?.id ?? round.key;

  const firstRun = rounds.find((round) => round.run !== undefined);
  const runLabels = rounds.filter((round) => round.run !== undefined).map((round) => round.label);

  return (
    <div className={styles.playByPlayList}>
      {rounds.map((round) => {
        if (round.run !== undefined) {
          return (
            <React.Fragment key={roundKey(round)}>
              {round === firstRun && round.holeCards.length > 0 && (
                // The viewer's hole cards ONCE, above the run rows (EN-11110
                // B6); a run row is "Run n" and its board only.
                <div className={styles.playByPlayRunsHole} data-runs-hole-cards=''>
                  <span className={styles.playByPlayRoundLabel}>Your cards</span>
                  <HoleCards cards={round.holeCards} revealed size='xsmall' />
                </div>
              )}
              <RunRound
                round={round}
                gameType={gameType}
                avatarByName={avatarByName}
                runResult={runResults?.[round.run]}
                runLabels={runLabels}
              />
            </React.Fragment>
          );
        }
        const streetTime = formatEventTime(round.street?.ts);
        const stackCards = round.holeCards.length > 2;
        const cards = (round.holeCards.length > 0 || round.boards.length > 0) && (
          <div className={styles.playByPlayRoundCards}>
            {(round.boards.length > 0 ? round.boards : [undefined]).map((board, index) => (
              <div key={index} className={styles.playByPlayRoundRun}>
                {round.holeCards.length > 0 && (
                  <HoleCards cards={round.holeCards} revealed size='xsmall' />
                )}
                {board && board.cards.length > 0 && (
                  <div className={styles.playByPlayRoundBoard}>
                    <CardStrip cards={board.cards} revealed size='xsmall' />
                  </div>
                )}
              </div>
            ))}
          </div>
        );
        return (
          <section
            key={roundKey(round)}
            className={styles.playByPlayRound}
            data-round={round.key}
            aria-label={round.label}>
            <header
              className={styles.playByPlayRoundHeader}
              data-street={round.street ? true : undefined}>
              <div className={styles.playByPlayRoundContent}>
                <span className={styles.playByPlayRoundLabel}>{round.label}</span>
                {round.street?.text && <span className={styles.srOnly}>{round.street.text}</span>}
                {!stackCards && cards}
                {streetTime && <span className={styles.playByPlayTime}>{streetTime}</span>}
              </div>
              {round.street && typeof round.street.totalPot === 'number' ? (
                <span className={styles.playByPlayPot}>
                  {formatPot(round.street.totalPot, gameType)}
                </span>
              ) : (
                <span className={styles.playByPlayRoundPotSpacer} aria-hidden='true' />
              )}
              {stackCards && cards}
            </header>
            <RoundRows events={round.events} gameType={gameType} avatarByName={avatarByName} />
          </section>
        );
      })}
    </div>
  );
};

/** A round's action rows, oldest first. */
const RoundRows = ({
  events,
  gameType,
  avatarByName
}: {
  events: HandHistoryEvent[];
  gameType: GameType;
  avatarByName?: AvatarByName;
}) => (
  <ol className={styles.playByPlayRows}>
    {events.map((event) => {
      const eventTime = formatEventTime(event.ts);
      return (
        <li key={event.id} className={styles.playByPlayItem}>
          <RowLead event={event} avatarByName={avatarByName} />
          <div className={styles.playByPlayContent}>
            <div className={styles.playByPlayMessage}>
              <span className={styles.playByPlayText}>{event.text}</span>
              {eventTime && <span className={styles.playByPlayTime}>{eventTime}</span>}
            </div>
          </div>
          <RowTrail event={event} gameType={gameType} />
        </li>
      );
    })}
  </ol>
);

/**
 * One run of a multi-run hand (EN-11110 B4/B6/B8): its result on its own line
 * — what the viewer won on it, else who won it — with the time and what that
 * run paid, then ONE line of "Run n" and that run's whole board, then its
 * payout rows. No Flop / Turn / River rows: the streets dealt after the all-in
 * are this board, and the rounds played before it keep their own containers
 * above the runs. The hole cards are shown once above the runs, never here, so
 * every run's cards line is the same shape and none wraps.
 *
 * The "Run n" row's own `totalPot` is the whole hand's pot (test1 Hand 27:
 * $19.02 on every run, which paid $6.34 each), so it is never shown here; a
 * run that has not paid yet has no result line at all.
 */
const RunRound = ({
  round,
  gameType,
  avatarByName,
  runResult,
  runLabels
}: {
  round: BettingRound;
  gameType: GameType;
  avatarByName?: AvatarByName;
  runResult?: RunResult;
  /** Every run's label, so this one is as wide as the widest (see below). */
  runLabels: string[];
}) => {
  const streetTime = formatEventTime(round.street?.ts);
  const board = round.boards[0]?.cards ?? [];
  return (
    <section className={styles.playByPlayRound} data-round={round.key} aria-label={round.label}>
      <header
        className={`${styles.playByPlayRoundHeader} ${styles.playByPlayRunHeader}`}
        data-street={round.street ? true : undefined}>
        {runResult && (
          <div className={styles.playByPlayRunResult} data-run-result-line=''>
            <span className={styles.playByPlaySummary}>{runResult.summary}</span>
            {streetTime && <span className={styles.playByPlayTime}>{streetTime}</span>}
            {runResult.amount > 0 && (
              <span className={styles.playByPlayPot}>{formatPot(runResult.amount, gameType)}</span>
            )}
          </div>
        )}
        <div className={styles.playByPlayRunCards} data-run-cards-line=''>
          {/* The label's cell is sized by every run's label (hidden), so it
              is as wide as the widest and every run's board starts at the same
              x — the runs are separate sections, with no grid to share. */}
          <span className={styles.playByPlayRunLabel}>
            {runLabels.map((label) => (
              <span
                key={label}
                className={`${styles.playByPlayRoundLabel} ${styles.playByPlayRunLabelSizer}`}
                data-sizer={label}
                aria-hidden='true'
              />
            ))}
            <span className={styles.playByPlayRoundLabel}>{round.label}</span>
          </span>
          {round.street?.text && <span className={styles.srOnly}>{round.street.text}</span>}
          {board.length > 0 && <CardStrip cards={board} revealed size='xsmall' />}
        </div>
      </header>
      <RoundRows events={round.events} gameType={gameType} avatarByName={avatarByName} />
    </section>
  );
};

/** The pre-grouping list the V1 and proposal layouts still use: newest first, cards under the rows that carry them. */
const FlatPlayByPlayList = ({
  events,
  gameType,
  avatarByName
}: {
  events: HandHistoryEvent[];
  gameType: GameType;
  avatarByName?: AvatarByName;
}) => (
  <div className={styles.playByPlayList}>
    {[...events]
      .sort((a, b) => b.order - a.order)
      .map((event) => {
        const eventTime = formatEventTime(event.ts);
        return (
          <div key={event.id} className={styles.playByPlayItem}>
            <RowLead event={event} avatarByName={avatarByName} />
            <div className={styles.playByPlayContent}>
              <div
                className={styles.playByPlayMessage}
                data-street={isStreetEvent(event) || undefined}>
                <span className={styles.playByPlayText}>{event.text}</span>
                {eventTime && <span className={styles.playByPlayTime}>{eventTime}</span>}
              </div>
              {Boolean(event.tableCards?.length) && (
                <div className={styles.playByPlayCards}>
                  <CardStrip cards={event.tableCards} revealed size='xsmall' />
                </div>
              )}
            </div>
            <RowTrail event={event} gameType={gameType} />
          </div>
        );
      })}
  </div>
);
